<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
    addresses: {
        type: Array,
        default: () => []
    },
    isEditing: {
        type: Boolean,
        default: false
    },
    showActions: {
        type: Boolean,
        default: true
    },
    fields: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['new-address', 'edit-address', 'delete-address']);

const tableColumns = computed(() => {
    return props.fields.filter((field) => field.visible !== false);
});

const showAddressList = computed(() => {
    return props.isEditing || props.addresses.length > 0;
});

function handleEditAddress(address) {
    emit('edit-address', address);
}

function handleDeleteAddress(address) {
    emit('delete-address', address);
}
</script>

<template>
    <div v-if="showAddressList" class="address-list">
        <div v-if="addresses.length > 0">
            <!-- Remover v-container y class="pa-0" -->
            <div class="table-container mt-3">
                <v-table density="compact">
                    <thead>
                        <tr>
                            <th
                                class="index-column sticky-column"
                                style="left: 0"
                            >
                                <div class="sticky-content"></div>
                            </th>

                            <th
                                v-for="column in tableColumns"
                                :key="column.model"
                                class="text-left"
                            >
                                {{ t(column.label) }}
                            </th>
                            <th v-if="isEditing" class="text-center">
                                {{ t('Actions') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(address, index) in addresses"
                            :key="address.id"
                            :class="{
                                'bg-primary-lighten-5': address.isDefault
                            }"
                            class="hover-row data-row"
                        >
                            <td
                                class="index-column sticky-column"
                                style="left: 0"
                            >
                                <div class="sticky-content index-content">
                                    <span class="row-number">{{
                                        index + 1
                                    }}</span>
                                </div>
                            </td>

                            <td
                                v-for="column in tableColumns"
                                :key="column.model"
                            >
                                <div class="d-flex align-center cell-content">
                                    <template
                                        v-if="column.model === 'isDefault'"
                                    >
                                        <div class="cell-content">
                                            <v-icon
                                                v-if="address[column.model]"
                                                color="success"
                                                size="small"
                                                >mdi-check</v-icon
                                            >
                                            <span v-else class="text-grey"
                                                >-</span
                                            >
                                        </div>
                                    </template>

                                    <template v-else>
                                        <span
                                            class="text-cell"
                                            :class="{
                                                'text-wrap':
                                                    column.model ===
                                                    'formattedAddress'
                                            }"
                                        >
                                            {{ address[column.model] ?? '-' }}
                                        </span>
                                    </template>
                                </div>
                            </td>

                            <td
                                v-if="isEditing"
                                class="text-center actions-cell"
                            >
                                <div
                                    v-if="!address.isDefault"
                                    class="cell-content row-actions"
                                >
                                    <v-btn
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        @click="handleEditAddress(address)"
                                    >
                                        <v-icon>mdi-pencil</v-icon>
                                        <v-tooltip activator="parent">
                                            {{ t('Edit') }}
                                        </v-tooltip>
                                    </v-btn>

                                    <v-btn
                                        size="small"
                                        variant="text"
                                        color="error"
                                        @click="handleDeleteAddress(address)"
                                    >
                                        <v-icon>mdi-delete</v-icon>
                                        <v-tooltip activator="parent">
                                            {{ t('Delete') }}
                                        </v-tooltip>
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </div>
        </div>
    </div>
</template>

<style scoped>
.address-list {
    margin-top: 0; /* Sin margen superior */
    padding: 0; /* Sin padding */
}

.table-container {
    margin: 0;
    padding: 0;
    width: 100%;
}

.v-table {
    border-radius: 0;
    margin: 0;
    width: 100%;
}

/* Asegurar que no haya padding adicional */
:deep(.v-container) {
    margin: 0 !important;
    padding: 0 !important;
}

/* Remove spacing and borders */
:deep(.v-table) {
    border-spacing: 0;
    border-collapse: collapse;
    border: none !important;
    margin: 0 !important;
}

.required-mark {
    color: red;
    margin-left: 2px;
}

.text-right {
    text-align: right;
}

/* Remove spacing and borders */
:deep(.v-table) {
    border-spacing: 0;
    border-collapse: collapse;
    border: none !important;
}

/* Table cell borders */
:deep(.v-table td) {
    padding: 0 !important;
    height: 32px !important;
    line-height: 1 !important;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

:deep(.v-table th) {
    padding: 0 !important;
    height: 32px !important;
    line-height: 1 !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
    border-right: none !important;
}

/* Remove last column right border */
:deep(.v-table td:last-child) {
    border-right: none !important;
}

/* Input styling without borders */
:deep(.v-field) {
    border-radius: 0 !important;
    min-height: 32px !important;
    margin-bottom: 0 !important;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

:deep(.v-field__outline) {
    display: none !important;
}

/* Add focus highlight */
:deep(.v-field--focused) {
    border: 2px solid rgb(var(--v-theme-primary)) !important;
    background: rgb(var(--v-theme-surface)) !important;
}

/* Reduce field and label sizing */
:deep(.v-field__input) {
    min-height: 28px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 28px !important;
    font-size: 0.875rem !important;
}

:deep(.v-field__label) {
    font-size: 0.75rem !important;
    margin-top: -2px !important;
}

:deep(.v-field) {
    min-height: 28px !important;
}

/* Adjust table cell height */
:deep(.v-table td),
:deep(.v-table th) {
    padding: 0 !important;
    height: 28px !important;
    line-height: 1 !important;
}

/* Remove row spacing */
:deep(.v-table tbody tr) {
    height: 28px !important;
}

/* Make sure action buttons don't affect row height */
:deep(.v-btn--size-small) {
    height: 24px !important;
    width: 24px !important;
}

.index-column {
    width: 1px;
    white-space: nowrap;
    padding: 0 8px !important;
    text-align: center;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.7);
    margin: 0 4px !important;
    min-width: 48px !important;
    position: relative;
}

.index-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.row-number {
    flex-shrink: 0;
}

.modified-indicator {
    flex-shrink: 0;
}

/* Remove border only from index column header */
:deep(.v-table th:first-child) {
    border-right: none !important;
}

/* Keep border for index column data cells */
:deep(.v-table td:first-child) {
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Add table wrapper borders */
:deep(.v-table .v-table__wrapper) {
    border-left: 1px solid rgba(0, 0, 0, 0.08) !important;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Header specific styles */
:deep(.v-table thead tr) {
    margin: 0 -1px !important;
    border-left: 1px solid transparent !important;
    border-right: 1px solid transparent !important;
}

:deep(.v-table thead th) {
    border-right: none !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Remove duplicate styles and keep only what's needed */
:deep(.v-table td) {
    padding: 0 !important;
    height: 32px !important;
    line-height: 1 !important;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Last cell in each row */
:deep(.v-table td:last-child) {
    border-right: none !important;
}

/* Cell content wrapper to handle padding within strict height */
.cell-content {
    padding: 4px 8px;
    height: 28px;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
}

.text-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.text-wrap {
    white-space: normal !important;
    word-wrap: break-word;
    max-width: 200px;
}

.row-actions {
    justify-content: center;
    gap: 2px;
}

.hover-row:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

:deep(.v-btn--size-small) {
    height: 24px !important;
    width: 24px !important;
}
</style>
